import React from 'react';
import { useTheme } from '../../context/ThemeContext';

const StatChart = ({ data, options = {}, fieldConfig = {} }) => {
  const { isDark } = useTheme();
  
  // Data can be either a stat object or will be calculated
  const stats = data.current !== undefined ? data : { current: 0 };
  
  const value = stats.current || 0;
  const unit = fieldConfig.defaults?.unit || '';
  
  // Get color mode
  const colorMode = options.colorMode || 'value';
  const graphMode = options.graphMode || 'none';
  const textSize = options.textSize || 'auto';
  
  // Thresholds
  const thresholds = fieldConfig.defaults?.thresholds?.steps || [
    { value: 0, color: 'green' }
  ];

  const getColor = (val) => {
    const sortedThresholds = [...thresholds].sort((a, b) => b.value - a.value);
    for (let threshold of sortedThresholds) {
      if (val >= threshold.value) {
        return threshold.color;
      }
    }
    return thresholds[0]?.color || 'green';
  };

  const color = getColor(value);
  
  // Grafana-like color palette
  const colorMap = isDark ? {
    green: '#73BF69',
    yellow: '#F2CC0C',
    red: '#F2495C',
    blue: '#5794F2',
    orange: '#FF9830'
  } : {
    green: '#7EB26D',
    yellow: '#EAB839',
    red: '#E24D42',
    blue: '#1F78C1',
    orange: '#EF843C'
  };
  
  const textColor = colorMap[color] || color;
  const backgroundColor = colorMode === 'background' 
    ? (isDark ? `${textColor}30` : `${textColor}20`)
    : 'transparent';

  // Calculate font size based on panel size and value length
  const valueStr = value.toLocaleString();
  const baseFontSize = textSize === 'auto' 
    ? Math.min(64, Math.max(32, 400 / Math.max(valueStr.length, 3)))
    : 48;

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '100%',
      backgroundColor: backgroundColor,
      borderRadius: '2px',
      padding: '16px'
    }}>
      <div style={{ textAlign: 'center', width: '100%' }}>
        <div style={{ 
          fontSize: `${baseFontSize}px`, 
          fontWeight: 500, 
          color: colorMode === 'background' 
            ? (isDark ? '#e6edf3' : '#262626')
            : textColor,
          lineHeight: 1.2,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          letterSpacing: '-0.02em'
        }}>
          {valueStr}
        </div>
        {unit && (
          <div style={{ 
            fontSize: `${Math.max(12, baseFontSize * 0.25)}px`, 
            color: isDark ? '#8b949e' : '#8c8c8c', 
            marginTop: '4px',
            fontWeight: 400
          }}>
            {unit}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatChart;